import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { reactHookForm } from '@yola/ws-ui';
import {
  dialogs,
  siteLocales,
  integration,
  i18next,
  site,
  view,
  dispatchAwaitableAction,
} from '@yola/ws-sdk';
import { constants as subscriptionManagerConstants } from '@yola/subscription-manager-js';
import segment from 'src/js/modules/analytics/segment';
import isFeatureAvailable from 'src/js/modules/upsell/verifiers/is-feature-available';
import user from 'yola-editor/src/js/modules/user';
import { saveSiteWithEffect } from 'src/js/modules/saving/helpers/save-site';
import store from 'src/js/store';
import AddLanguageDialog from '../components/add-language-dialog';
import dialogTypes from '../../dialogs/constants/dialog-types';
import useCheckForUnsafe from '../hooks/use-check-for-unsafe';
import getLanguages from '../helpers/get-languages';
import getCountries from '../helpers/get-countries';
import getRelatedLanguageOptions from '../helpers/get-related-language-options';
import ai from '../../ai';
import upsell from '../../upsell';
import reloadSiteMeta from '../../ai-wizard/helpers/reload-site-meta';
import updateLanguageSelector from '../helpers/update-language-selector';

const {
  hostingPackageFeatureNames: { AI_LOCALIZATION, SITE_LANGUAGES },
} = subscriptionManagerConstants;

const { useForm } = reactHookForm;
const {
  trackers: { trackEvent },
  constants: { events },
} = segment;

const fieldNames = {
  LOCALE: 'locale',
  TRANSLATE_WITH_AI: 'translateWithAi',
};

const defaultInitialValues = Object.freeze({});

function AddLanguageDialogContainer({
  initialValues = defaultInitialValues,
  onClose,
  onDialogMainAction,
  onDialogCancel,
}) {
  const dispatch = useDispatch();
  const upsells = useSelector(integration.selectors.getUpsells);
  const pages = useSelector(site.selectors.getPages);
  const mainLocale = useSelector(siteLocales.selectors.getMainLocale);
  const userPreferences = useSelector(
    (state) => integration.selectors.getSettings(state).userPreferences
  );
  const availablePlatformComponents = useSelector(user.selectors.getAvailablePlatformComponents);

  const locales = useMemo(() => siteLocales.accessors.getSiteLocales(), []);
  const langMap = useMemo(() => getLanguages(), []);
  const countriesMap = useMemo(() => getCountries(), []);

  const isAiLocalizationAvailable = isFeatureAvailable(AI_LOCALIZATION);

  const mainLocalePagesCount = useMemo(
    () => pages.filter((page) => page.locale === mainLocale).length,
    [pages, mainLocale]
  );

  const languageOptions = useMemo(
    () =>
      getRelatedLanguageOptions(locales, langMap, countriesMap).map((item) => ({
        value: item.code,
        iconComponent: item.iconComponent,
        label: item.name,
      })),
    [langMap, countriesMap, locales]
  );

  const computedInitialValues = useMemo(() => {
    const firstLangCode = languageOptions[0].value;
    return {
      [fieldNames.LOCALE]: initialValues.locale || firstLangCode,
      [fieldNames.TRANSLATE_WITH_AI]: initialValues.translateWithAi || false,
    };
    // eslint-disable-next-line yola/react-hooks/exhaustive-deps
  }, []);

  const { control, handleSubmit, getValues, setValue } = useForm({
    defaultValues: computedInitialValues,
    mode: 'onTouched',
  });

  const checkForUnsafe = useCheckForUnsafe();

  const showProgressDialog = useCallback(
    (jobData) => {
      const succeeded = jobData?.results?.succeeded || {};
      const current = Object.values(succeeded).flat().length || 1;

      dialogs.operations.show(dialogTypes.TRANSLATION_PROGRESS_DIALOG, {
        overlay: 'visible',
        cancelable: false,
        progressType: 'indeterminate',
        width: 336,
        height: 109,
        minHeight: 109,
        captions: {
          title: `${i18next.t('Translating {current}/{total}...', {
            current,
            total: mainLocalePagesCount,
          })}`,
        },
      });
    },
    [mainLocalePagesCount]
  );

  const handleAddLocale = useCallback(
    async (values) => {
      const { [fieldNames.LOCALE]: locale, [fieldNames.TRANSLATE_WITH_AI]: translateWithAi } =
        values;
      const wasMonolingualSite = !site.verifiers.isMultilingualSite();

      showProgressDialog();

      try {
        await siteLocales.helpers.initiateLanguageCreation({
          locale,
          translateWithAi,
          onPoll: showProgressDialog,
          ...(translateWithAi && {
            interval: ai.helpers.createTranslationPollInterval({ total: mainLocalePagesCount }),
          }),
        });
      } catch (e) {
        ai.helpers.reportTranslationError({ error: e, flow: 'add-language', locale });

        if (translateWithAi) {
          dispatch(
            dialogs.actions.show(dialogTypes.TRANSLATION_ERROR_DIALOG, {
              onRetry: () => {
                handleAddLocale(values);
              },
            })
          );
        } else {
          dialogs.operations.show(dialogTypes.ERROR_DIALOG, {
            captions: {
              title: i18next.t('Error during language addition'),
              description: i18next.t('An error occurred while adding the new language'),
            },
          });
        }

        return;
      }

      await reloadSiteMeta();
      trackEvent(events.WEBSITE_LANGUAGE_ADDED, { languageCode: locale });
      await updateLanguageSelector();

      const state = store.getState();
      const activePageId = site.selectors.getHomePage(state, locale).id;

      await dispatchAwaitableAction(site.actions.setActivePage(activePageId));
      await saveSiteWithEffect(() => {
        dispatch(view.actions.forceReloadView());

        if (wasMonolingualSite && !userPreferences.multilingualTourCompleted) {
          dialogs.operations.show(dialogTypes.MULTILINGUAL_TOUR, {
            onClose,
          });
          return;
        }

        onClose();
      });
    },
    [showProgressDialog, onClose, mainLocalePagesCount, dispatch, userPreferences]
  );

  const showSelf = useCallback(() => {
    dialogs.operations.show(dialogTypes.ADD_LANGUAGE, {
      onClose,
      initialValues: getValues(),
    });
  }, [onClose, getValues]);

  const guardedSubmit = useMemo(
    () =>
      checkForUnsafe({
        onSubmit: handleAddLocale,
        onCancel: showSelf,
      }),
    [checkForUnsafe, handleAddLocale, showSelf]
  );

  const submitWithLimitCheck = useCallback(
    async (values) => {
      try {
        await upsell.operations.requestFeatureAvailability(SITE_LANGUAGES, {
          count: locales.length + 1,
        });
      } catch (error) {
        if (error instanceof Error) {
          throw error;
        }
        showSelf();
        // eslint-disable-next-line no-console
        console.log('Upgrade flow was canceled');
        return;
      }

      guardedSubmit(values);
    },
    [guardedSubmit, locales.length, showSelf]
  );

  const handleDialogCancel = useCallback(() => {
    dialogs.operations.hide();
    onDialogCancel();
    onClose();
  }, [onDialogCancel, onClose]);

  const onFormSubmit = useMemo(
    () =>
      handleSubmit((values) => {
        onDialogMainAction({
          targetLocale: values[fieldNames.LOCALE],
          aiTranslation: values[fieldNames.TRANSLATE_WITH_AI],
        });
        submitWithLimitCheck(values);
      }),
    [handleSubmit, onDialogMainAction, submitWithLimitCheck]
  );

  const onTranslateWithAiClick = useCallback(() => {
    if (!isAiLocalizationAvailable) {
      setValue(fieldNames.TRANSLATE_WITH_AI, false);
      dialogs.operations.hide();
      const { captions, premiumFeature, onUpgrade, purchaseFlowType } = upsells.aiLocalization;

      // b2c & reseller platform upgrade
      if (availablePlatformComponents.includes(user.platformComponents.PAYMENTS)) {
        onUpgrade().catch(() => {
          showSelf();
        });
        return;
      }

      dialogs.operations.show(dialogTypes.UPGRADE, {
        purchaseFlowType,
        premiumFeature,
        captions,
        onUpgrade,
        onUpgradeReject: showSelf,
      });
    }
  }, [isAiLocalizationAvailable, upsells, showSelf, availablePlatformComponents, setValue]);

  const onTranslationGuidelinesClick = () => {
    const { locale } = getValues();

    dialogs.operations.show(dialogTypes.AI_TRANSLATION_GUIDELINES_DIALOG, {
      onAfterSave: showSelf,
      onAfterCancel: showSelf,
      onAfterFail: showSelf,
      locales: [locale],
      activeLocale: locale,
    });
  };

  return (
    <AddLanguageDialog
      control={control}
      languageOptions={languageOptions}
      onFormSubmit={onFormSubmit}
      onCancel={handleDialogCancel}
      isAiLocalizationAvailable={isAiLocalizationAvailable}
      onTranslateWithAiClick={onTranslateWithAiClick}
      onTranslationGuidelinesClick={onTranslationGuidelinesClick}
    />
  );
}

AddLanguageDialogContainer.propTypes = {
  initialValues: PropTypes.shape({
    locale: PropTypes.string,
    translateWithAi: PropTypes.bool,
  }),
  onClose: PropTypes.func.isRequired,
  onDialogMainAction: PropTypes.func.isRequired,
  onDialogCancel: PropTypes.func.isRequired,
};

export default AddLanguageDialogContainer;
